import { toast } from "sonner";

const LAST_RELOAD_KEY = "stale-chunk-reload-at";
const MIN_RELOAD_INTERVAL_MS = 60_000;

// Every browser phrases a failed dynamic import differently; Vite adds its own
// message for a stylesheet that a chunk depends on.
const CHUNK_LOAD_ERROR_MESSAGES = [
  "failed to fetch dynamically imported module", // Chrome
  "error loading dynamically imported module", // Firefox
  "importing a module script failed", // Safari
  "unable to preload css for", // Vite's __vitePreload
];

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  const normalized = message.toLowerCase();
  return CHUNK_LOAD_ERROR_MESSAGES.some((candidate) =>
    normalized.includes(candidate),
  );
}

// After a deploy, the previous build's hashed chunks are no longer served, so
// lazy imports from stale tabs fail. Vite reports those failures via
// "vite:preloadError"; reload right away to pick up the new version.
export function reloadOnStaleChunk() {
  // Deliberately no `event.preventDefault()`: cancelling the event makes Vite's
  // __vitePreload swallow the rejection and resolve the dynamic import with
  // `undefined`, so the `lazy()` factory then throws "Cannot read properties of
  // undefined (reading '<PageName>')" and the router's error screen wins the
  // race against the reload below. Letting the rejection through keeps
  // ChunkErrorBoundary in charge of what the user sees.

  window.addEventListener("vite:preloadError", () => {
    const lastReload = Number(sessionStorage.getItem(LAST_RELOAD_KEY) ?? 0);
    if (Date.now() - lastReload < MIN_RELOAD_INTERVAL_MS) {
      // Reloading didn't fix the missing chunk; surface the error instead of
      // reloading in a loop.
      toast.error("A new version of Midship is required", {
        description:
          "Please refresh the page. If the problem persists, contact support.",
        duration: Infinity,
      });
      return;
    }
    sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
    window.location.reload();
  });
}
